import { ARENA_RAKE_BPS_DIVISOR, ARENA_RAKE_BPS_STEP } from "./rake";
import { Decimal } from "decimal.js";
import { z } from "zod";

// ────────────────────────── Cede Arena referral programme ─────────────────────
//
// A referrer earns a share of each referee's settled buy-in on paid classic 1v1
// duels. The share scales with the combined qualifying volume of all the referrer's
// referees, so one rate applies to every referee, and it is taken out of Cede's
// platform rake, never the creator's cut and never margin.
//
// Kept out of the cede-arena barrel's own file so the CMS and API can import the
// schema without pulling the whole index.

/** The one USDT decimal-string rule, exported so the CMS validates what the API accepts. */
export const REFERRAL_USDT_REGEX = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;

/** A non-negative USDT decimal string (up to 6 dp, the ledger's precision). */
const ReferralUsdtSchema = z.string().regex(REFERRAL_USDT_REGEX, {
  error: "must be a non-negative decimal with up to 6 decimals",
});

/** Ceiling on authored tiers. A ladder longer than this is a config mistake, not a design. */
export const ARENA_REFERRAL_MAX_TIERS = 10;

/**
 * Ceiling on a tier's rate: 100% of the house rake, and not a basis point more.
 *
 * This is what makes a referral structurally unable to cost Cede money. A rate is a
 * SHARE OF THE RAKE rather than of the buy-in, so the worst an authored ladder can do
 * is hand the whole platform cut to the referrer.
 */
export const ARENA_REFERRAL_MAX_RATE_BPS = ARENA_RAKE_BPS_DIVISOR;

/**
 * Ten years; `null` already means never. Not the int4 ceiling: that many days overflows a
 * `Date`, and an invalid expiry compares false, so the window would never close.
 */
export const ARENA_REFERRAL_MAX_EXPIRY_DAYS = 3650;

/** Rows per page of the referee roster. */
export const ARENA_REFERRAL_PAGE_SIZE = 25;

/**
 * One slice of the ladder: from `fromUsdt` of the combined qualifying volume of the
 * referrer's referees (inclusive) until the next tier's threshold, the referrer earns
 * `rateBps` OF THE HOUSE RAKE taken on each buy-in.
 *
 * A share of the rake, never of the buy-in: at a 50% rate and a 10% house rake the
 * referrer earns 5% of the stake, and if the rake is ever set to 0 they earn nothing,
 * automatically. That is the whole point of expressing it this way: a referral is paid
 * out of revenue that exists, so no ladder and no rake can combine into a loss.
 *
 * Modelled as ascending open-ended thresholds rather than explicit from/to pairs
 * so gaps and overlaps cannot be expressed at all. The last tier runs to infinity.
 */
export const ArenaReferralTierSchema = z.object({
  fromUsdt: ReferralUsdtSchema,
  /**
   * Same basis-point grid as the rake it is a share of, so the two stay directly
   * comparable with no rounding seam between them.
   */
  rateBps: z
    .number()
    .int()
    .min(0, { error: "rate must be at least 0% of the house rake" })
    .max(ARENA_REFERRAL_MAX_RATE_BPS, { error: "rate must be at most 100% of the house rake" })
    .refine((bps) => bps % ARENA_RAKE_BPS_STEP === 0, {
      error: "rate must be a multiple of 0.25% (25 bps)",
    }),
});
export type ArenaReferralTier = z.infer<typeof ArenaReferralTierSchema>;

/**
 * The ladder: ascending, starting at 0 so every volume maps to exactly one tier.
 * A ladder that starts above 0 would leave a referee's first buy-ins earning
 * nothing with no way to express that intent, which reads as a config bug.
 */
export const ArenaReferralTiersSchema = z
  .array(ArenaReferralTierSchema)
  .min(1)
  .max(ARENA_REFERRAL_MAX_TIERS)
  .refine((tiers) => tiers[0]?.fromUsdt === "0", { error: "the first tier must start at 0" })
  .refine(
    (tiers) =>
      tiers.every((tier, index) => {
        const previous = tiers[index - 1];
        if (previous == null) return true;
        // Re-tested because zod runs an array-level refine even when an element's own
        // regex failed, and `new Decimal("abc")` throws rather than comparing false.
        if (!REFERRAL_USDT_REGEX.test(tier.fromUsdt) || !REFERRAL_USDT_REGEX.test(previous.fromUsdt)) return false;
        return new Decimal(tier.fromUsdt).gt(previous.fromUsdt);
      }),
    { error: "tier thresholds must be strictly ascending" },
  );

/**
 * Global referral config, backed by the singleton `arena_referral_configs` row and
 * edited from the Cede Arena section of internal-cms.
 *
 * `rewardExpiryDays` is read live on every accrual rather than stamped onto each
 * referral, so editing it moves the window for every existing referral at once.
 * That is deliberate: expiry gates future accrual only, so a change can never
 * remove rewards already earned.
 */
export const ArenaReferralConfigDtoSchema = z.object({
  /**
   * Qualifying volume a referrer must settle before their code unlocks.
   *
   * Must be positive. At 0 a code mints on mere page load, which removes the only
   * structural bar to mutual referral: a referrer has necessarily played to earn a code,
   * and having played is exactly what disqualifies them from being referred.
   */
  unlockThresholdUsdt: ReferralUsdtSchema.refine(
    (value) => REFERRAL_USDT_REGEX.test(value) && new Decimal(value).gt(0),
    { error: "unlock threshold must be greater than 0" },
  ),
  tiers: ArenaReferralTiersSchema,
  /**
   * Days from a referee's first competition during which their buy-ins accrue. `null` ⇒
   * never expires.
   */
  rewardExpiryDays: z.number().int().positive().max(ARENA_REFERRAL_MAX_EXPIRY_DAYS).nullable(),
  /**
   * Cap on a managed KOL's `kolShareBps + managerShareBps`, in bps of the platform rake.
   * The API refuses a value below a split already in force.
   */
  managerMaxShareBps: z
    .number()
    .int()
    .min(0)
    .max(ARENA_REFERRAL_MAX_RATE_BPS)
    .refine((bps) => bps % ARENA_RAKE_BPS_STEP === 0, { error: "the cap must be a multiple of 0.25% (25 bps)" }),
});
export type ArenaReferralConfigDto = z.infer<typeof ArenaReferralConfigDtoSchema>;

/** Response payload for `GET /v1/cms/arena-referral-settings`. */
export const GetArenaReferralConfigResponseSchema = ArenaReferralConfigDtoSchema;
export type GetArenaReferralConfigResponse = z.infer<typeof GetArenaReferralConfigResponseSchema>;

/** Body for `PUT /v1/cms/arena-referral-settings`. */
export const UpdateArenaReferralConfigBodySchema = ArenaReferralConfigDtoSchema;
export type UpdateArenaReferralConfigBody = z.infer<typeof UpdateArenaReferralConfigBodySchema>;

// ──────────────────────── Per-referrer ladder overrides ───────────────────────
//
// A signed KOL deal replaces the global ladder for one referrer. No row means
// standard terms, which is also the kill path: delete it and they revert on their
// next buy-in. Rewards already earned keep the rate they were earned at, because
// `ArenaReferralReward.rateBps` is stamped at accrual time.

/** Free-text on why a deal exists. Long enough for a link and a sentence, short enough to render in a cell. */
export const ARENA_REFERRAL_OVERRIDE_NOTE_MAX = 500;

/**
 * `null` inherits the global window. A number is this referrer's own window; a lifetime deal
 * uses ARENA_REFERRAL_MAX_EXPIRY_DAYS. Exported so the domain entity validates with this rule.
 */
export const ArenaReferralOverrideExpiryDaysSchema = z
  .number()
  .int({ error: "the reward expiry must be a whole number of days" })
  .positive({ error: "the reward expiry must be at least one day" })
  .max(ARENA_REFERRAL_MAX_EXPIRY_DAYS)
  .nullable();

/**
 * The overridable half of the config.
 *
 * `tiers` is required: an override with no ladder would be a row that does nothing.
 * The other two are null-means-inherit, so a rate-only deal says nothing about
 * unlocking or expiry rather than restating the global values and drifting from them.
 */
const ArenaReferralOverrideTermsSchema = z.object({
  tiers: ArenaReferralTiersSchema,
  /**
   * `null` inherits the global threshold. `0` waives the unlock outright, which the
   * global config forbids: that rule exists because codes are self-serve and a
   * zero-threshold world lets two strangers refer each other, and an override row is
   * created by hand for someone already chosen.
   */
  unlockThresholdUsdt: z
    .string()
    .regex(REFERRAL_USDT_REGEX, { error: "must be a non-negative decimal with up to 6 decimals" })
    .nullable(),
  rewardExpiryDays: ArenaReferralOverrideExpiryDaysSchema,
  /** Why this deal exists, for whoever asks in six months. */
  note: z.string().max(ARENA_REFERRAL_OVERRIDE_NOTE_MAX).nullable(),
});

/** Body for `/v1/cms/arena-referral-settings/overrides`: POST creates a deal, PUT edits the one `hubUserId` holds. */
export const SaveArenaReferralOverrideBodySchema = ArenaReferralOverrideTermsSchema.extend({
  hubUserId: z.number().int().positive(),
});
export type SaveArenaReferralOverrideBody = z.infer<typeof SaveArenaReferralOverrideBodySchema>;

/** One row of the CMS overrides table, carrying enough identity to recognise the referrer. */
export const ArenaReferralOverrideDtoSchema = SaveArenaReferralOverrideBodySchema.extend({
  /** Arena nickname, `null` when the account never set one. */
  nickname: z.string().nullable(),
  /** Wallet address, `null` for an email-OTP account that never connected one. */
  defiAddress: z.string().nullable(),
  updatedAt: z.coerce.date(),
});
export type ArenaReferralOverrideDto = z.infer<typeof ArenaReferralOverrideDtoSchema>;

/** Response for `GET /v1/cms/arena-referral-settings/overrides/get-by-page`: newest change first. */
export const ListArenaReferralOverridesResponseSchema = z.object({
  items: z.array(ArenaReferralOverrideDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListArenaReferralOverridesResponse = z.infer<typeof ListArenaReferralOverridesResponseSchema>;

/** Response for POST, PUT and DELETE on `/v1/cms/arena-referral-settings/overrides`: whose deal was written. */
export const ArenaReferralOverrideWriteResponseSchema = z.object({
  hubUserId: z.number().int().positive(),
});
export type ArenaReferralOverrideWriteResponse = z.infer<typeof ArenaReferralOverrideWriteResponseSchema>;

// ─────────────────────────────── Arena-facing DTOs ────────────────────────────

/** One referee row on the referrals page. */
export const ArenaReferralRefereeDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  /** Arena nickname, already public on leaderboards. `null` when the account never set one. */
  nickname: z.string().nullable(),
  /**
   * This referee's lifetime qualifying volume (decimal USDT). Unlike `totalRefereeVolumeUsdt`
   * it keeps counting after their earning window closes.
   */
  volumeUsdt: z.string(),
  /** What this referee has earned the referrer so far, pending and claimed combined. */
  earnedUsdt: z.string(),
  /** When accrual from this referee stops, or `null` when the config sets no expiry. */
  expiresAt: z.coerce.date().nullable(),
});
export type ArenaReferralRefereeDto = z.infer<typeof ArenaReferralRefereeDtoSchema>;

/**
 * Who referred a user, for the confirmation the referee sees once their code lands.
 *
 * `nickname` is null for an account that never set one, which is common and not a
 * failure: the client shows generic copy rather than inventing a name.
 */
export const ArenaReferrerDtoSchema = z.object({
  nickname: z.string().nullable(),
});
export type ArenaReferrerDto = z.infer<typeof ArenaReferrerDtoSchema>;

/** Response payload for `GET /v1/cede-arena/referrals`, and for the claim POST, which returns the refreshed state. */
export const GetArenaReferralsResponseSchema = z.object({
  /** `null` until the referrer crosses the unlock threshold. */
  code: z.string().nullable(),
  /** The referrer's own qualifying volume, for the unlock progress bar. */
  qualifyingVolumeUsdt: z.string(),
  unlockThresholdUsdt: z.string(),
  isUnlocked: z.boolean(),
  /**
   * The volume the tier ladder is read at: qualifying volume of every non-banned referee,
   * counting only what settled inside each referee's earning window.
   */
  totalRefereeVolumeUsdt: z.string(),
  pendingRewardsUsdt: z.string(),
  claimedRewardsUsdt: z.string(),
  /** The authored ladder, so the page renders the same rates the accrual applies. */
  tiers: ArenaReferralTiersSchema,
  /**
   * Who invited the viewer, or null when nobody did. Present whether or not they have
   * unlocked a code of their own: being referred and referring are independent.
   */
  referrer: ArenaReferrerDtoSchema.nullable(),
  /**
   * One page of the roster, ordered by `earnedUsdt` biggest first. Render as given; do
   * not re-sort client-side, or the page's slice of a global ranking stops meaning
   * anything. A page is `ARENA_REFERRAL_PAGE_SIZE` rows at most.
   */
  referees: z.array(ArenaReferralRefereeDtoSchema),
  /**
   * Opaque continuation token for the next page, or null on the last one. Pass it back as
   * the `cursor` query parameter. It encodes the sort key, so a roster that changes
   * between pages cannot make a row repeat or go missing the way an offset would.
   */
  nextCursor: z.string().nullable(),
  /**
   * Whether the viewer can still enter someone's code: they have never been referred and
   * have never paid for a match (a PAID or Spin & Go duel, or a paid competition), and they
   * are not on a per-referrer deal. Drives the manual-entry fallback. The refusal to use the code of someone they referred is only
   * checked on submit.
   */
  canBeReferred: z.boolean(),
});
export type GetArenaReferralsResponse = z.infer<typeof GetArenaReferralsResponseSchema>;

/** How a referee came to be attributed: the two paths are tracked separately on purpose. */
export const ARENA_REFERRAL_SOURCE = {
  LINK: "link",
  MANUAL: "manual",
  /** A KOL manager's link to their KOL, made by an accepted invite or a CMS reassign. */
  KOL_MANAGER: "kol_manager",
} as const;
export type ARENA_REFERRAL_SOURCE = (typeof ARENA_REFERRAL_SOURCE)[keyof typeof ARENA_REFERRAL_SOURCE];

/**
 * Response for `POST /v1/cede-arena/referrals/apply`. Carries the referrer so the client
 * can name them in the toast without a second request. Null when the apply succeeded but
 * the referrer could not be resolved.
 */
export const ApplyArenaReferralCodeResponseSchema = z.object({
  referrer: ArenaReferrerDtoSchema.nullable(),
});
export type ApplyArenaReferralCodeResponse = z.infer<typeof ApplyArenaReferralCodeResponseSchema>;

/** Body for `POST /v1/cede-arena/referrals/apply`. */
export const ApplyArenaReferralCodeBodySchema = z.object({
  code: z.string().min(1).max(32),
  /**
   * Which path attributed them. Analytics only: the client is the only thing that
   * knows whether the code arrived on a link or was typed, and nothing about the
   * referral itself depends on it. Defaults to manual, so an older client that
   * omits it is recorded as the typed path it was using.
   */
  source: z.enum([ARENA_REFERRAL_SOURCE.LINK, ARENA_REFERRAL_SOURCE.MANUAL]).default(ARENA_REFERRAL_SOURCE.MANUAL),
});
export type ApplyArenaReferralCodeBody = z.infer<typeof ApplyArenaReferralCodeBodySchema>;

/**
 * Error codes the referral endpoints return in the RFC 9457 `type` field.
 *
 * Shared because the client has to tell two of them apart that carry the same HTTP
 * status: `REFERRALS_DISABLED` and `CODE_NOT_FOUND` are both 404, but only an unknown
 * code is permanent, and treating a disabled programme as permanent throws away an
 * invite the moment the kill switch is flipped. The API's `ArenaReferralErrorCode`
 * derives from this, so the two cannot drift.
 */
export const ARENA_REFERRAL_ERROR_CODE = {
  REFERRALS_DISABLED: "ARENA_REFERRAL_REFERRALS_DISABLED",
  CODE_NOT_FOUND: "ARENA_REFERRAL_CODE_NOT_FOUND",
  REFEREE_NOT_NEW: "ARENA_REFERRAL_REFEREE_NOT_NEW",
  REFEREE_ALREADY_REFERRED: "ARENA_REFERRAL_REFEREE_ALREADY_REFERRED",
  SELF_REFERRAL: "ARENA_REFERRAL_SELF_REFERRAL",
  REFERRAL_CYCLE: "ARENA_REFERRAL_CYCLE",
  REFEREE_HAS_DEAL: "ARENA_REFERRAL_REFEREE_HAS_DEAL",
  CONFIG_MISSING: "ARENA_REFERRAL_CONFIG_MISSING",
  CONFIG_INVALID: "ARENA_REFERRAL_CONFIG_INVALID",
  OVERRIDE_INVALID: "ARENA_REFERRAL_OVERRIDE_INVALID",
  OVERRIDE_USER_NOT_FOUND: "ARENA_REFERRAL_OVERRIDE_USER_NOT_FOUND",
  OVERRIDE_NOT_FOUND: "ARENA_REFERRAL_OVERRIDE_NOT_FOUND",
  OVERRIDE_ALREADY_EXISTS: "ARENA_REFERRAL_OVERRIDE_ALREADY_EXISTS",
  TERMS_UNRESOLVED: "ARENA_REFERRAL_TERMS_UNRESOLVED",
  KOL_MANAGERS_DISABLED: "ARENA_REFERRAL_KOL_MANAGERS_DISABLED",
  NOT_KOL_MANAGER: "ARENA_REFERRAL_NOT_KOL_MANAGER",
  KOL_MANAGER_ALREADY_EXISTS: "ARENA_REFERRAL_KOL_MANAGER_ALREADY_EXISTS",
  KOL_SPLIT_ABOVE_CAP: "ARENA_REFERRAL_KOL_SPLIT_ABOVE_CAP",
  KOL_CAP_BELOW_SPLITS: "ARENA_REFERRAL_KOL_CAP_BELOW_SPLITS",
  KOL_NOT_LINKED: "ARENA_REFERRAL_KOL_NOT_LINKED",
  KOL_IS_MANAGER: "ARENA_REFERRAL_KOL_IS_MANAGER",
  KOL_INVITE_NOT_FOUND: "ARENA_REFERRAL_KOL_INVITE_NOT_FOUND",
  KOL_INVITE_UNAVAILABLE: "ARENA_REFERRAL_KOL_INVITE_UNAVAILABLE",
  /** The signed-in account has no verified email yet: it must add the invited one first. */
  KOL_INVITE_EMAIL_REQUIRED: "ARENA_REFERRAL_KOL_INVITE_EMAIL_REQUIRED",
  /** The signed-in account's verified email is not the one the invite was made for. */
  KOL_INVITE_EMAIL_MISMATCH: "ARENA_REFERRAL_KOL_INVITE_EMAIL_MISMATCH",
} as const;
export type ARENA_REFERRAL_ERROR_CODE = (typeof ARENA_REFERRAL_ERROR_CODE)[keyof typeof ARENA_REFERRAL_ERROR_CODE];

// ──────────────────────────────── CMS roster DTOs ─────────────────────────────

/** Page size the CMS loads a referrer's referees in. */
export const CMS_ARENA_REFERRAL_REFEREES_PAGE_SIZE = 50;

/** One referrer on the CMS roster. Counts include banned referrals: abuse stays visible to ops. */
export const CmsArenaReferrerDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  refereeCount: z.number().int().nonnegative(),
  /** Referees who have settled at least one qualifying competition. */
  playedRefereeCount: z.number().int().nonnegative(),
  /** Pending + claimed, across every referee. */
  earnedUsdt: z.string(),
});
export type CmsArenaReferrerDto = z.infer<typeof CmsArenaReferrerDtoSchema>;

/** Response for `GET /v1/cms/arena-referrals/referrers/get-by-page`: referee count desc. */
export const ListCmsArenaReferrersResponseSchema = z.object({
  items: CmsArenaReferrerDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaReferrersResponse = z.infer<typeof ListCmsArenaReferrersResponseSchema>;

/** One referee of a referrer, as the CMS sees it. */
export const CmsArenaRefereeDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** A manager's links to their KOLs are never listed. */
  source: z.enum([ARENA_REFERRAL_SOURCE.LINK, ARENA_REFERRAL_SOURCE.MANUAL]),
  attributedAt: z.coerce.date(),
  /** Null until they settle their first qualifying competition. */
  firstCompetitionAt: z.coerce.date().nullable(),
  /** Null when the config sets no expiry or the referee has not played yet. */
  expiresAt: z.coerce.date().nullable(),
  /** Pending + claimed. */
  earnedUsdt: z.string(),
  /** Settled duels that accrued a reward. */
  rewardedDuelCount: z.number().int().nonnegative(),
});
export type CmsArenaRefereeDto = z.infer<typeof CmsArenaRefereeDtoSchema>;

/** Response for `GET /v1/cms/arena-referrals/referees/get-by-page`: earned desc, then newest joined. */
export const ListCmsArenaRefereesResponseSchema = z.object({
  items: CmsArenaRefereeDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaRefereesResponse = z.infer<typeof ListCmsArenaRefereesResponseSchema>;
